/**
 * App-wide storage over-limit banner (MOTE-162).
 *
 * Mounted once in `AppShell`, below the header, so it stays constantly
 * visible on every authenticated page while the workspace is over its
 * storage quota — until usage is brought back under the limit. Shown only
 * to owners/admins (the people who can resolve it: free space or upgrade);
 * other members get the bell notification + the Settings meter, not a
 * persistent app-wide bar they can't act on.
 *
 * Reads the over-cap state from `useOrganization`; the query is gated to
 * owners/admins so non-managers never fetch it.
 *
 * Over-limit is restrict-never-destroy: uploads pause while over, files are
 * never deleted automatically, so the copy carries no date or deletion
 * warning.
 */

import { Link } from '@tanstack/react-router'
import { AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useMe } from '@/features/me/use-me'
import { isStorageAudience } from './storage-audience'
import { useOrganization } from './use-organization'

export function StorageOverLimitBanner() {
  const { t } = useTranslation('common')
  const me = useMe()
  const canManage = isStorageAudience(me.data)

  const org = useOrganization({ enabled: canManage })
  const overCapSince = org.data?.storageOverCapSince ?? null

  if (!canManage || !overCapSince) return null

  return (
    <div
      role="alert"
      className="flex items-center gap-3 border-b border-danger/30 bg-danger/10 px-4 py-2 text-sm text-danger-ink sm:px-6"
    >
      <AlertTriangle aria-hidden="true" className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1">{t('storageBanner.body')}</span>
      <Link
        to="/settings"
        search={{ tab: 'my-organization' }}
        className="shrink-0 font-semibold underline underline-offset-2"
      >
        {t('storageBanner.cta')}
      </Link>
    </div>
  )
}
